import { FC, useMemo } from 'react';

import classNames from 'classnames';

import { formatDateLocaleDDMMYY } from '../../utils/dateUtils';
import { Check } from '../icons/check';
import { Cross } from '../icons/cross';

import { ITariffToolData, TariffTypes } from '../../store/tariff.slice';

import { TariffCardLabel } from './tariff-card-label';
import { TariffCardHeader } from './tariff-card-header';
import { BonusInfoLabel } from './bonus-info-label';
import { ToolDescriptionIcon } from './tool-description-icon';
import { ITariffCardProps } from './tariff-card.types';

export const BaseTariffCard: FC<ITariffCardProps> = ({
  tariff,
  displayMode,
  onDetailsClick,
  onSelectClick,
  tariffToolsMaxCount,
  active,
  scheduled,
  start_end_date,
  selectDisabled,
  hideRenew,
}) => {
  const isShotMode = displayMode === 'shot';
  const isPromoAction = tariff.type === TariffTypes.PROMO;
  const isFree = tariff.type === TariffTypes.FREE || tariff.price === 0;
  const toolItems: ITariffToolData[] = useMemo(() => {
    if (tariff.tools_data && tariff.tools_data.length) {
      return tariff.tools_data;
    }

    const fallbackNames = tariff.tool_names || [];
    const fallbackLabels = tariff.tool_labels || [];

    return fallbackNames.map((toolName, index) => ({
      name: fallbackLabels[index] ?? toolName,
      tool_name: toolName,
      tool_description: null,
    }));
  }, [tariff.tool_labels, tariff.tool_names, tariff.tools_data]);

  const discount = tariff.base_discount;
  const hasDiscount = discount > 0 && discount < 100;
  const baseDuration = Number(tariff.base_duration || 30);
  const basePrice = Number(tariff.base_price || 0);
  const discountedBasePrice = Math.round(basePrice * ((100 - discount) / 100));

  const tariffToolsFiller = useMemo(() => {
    const leftCount = tariffToolsMaxCount - toolItems.length;
    if (leftCount > 0) {
      return Array.from({ length: leftCount }).map((_, index) => {
        return <div key={`tool-filler-${index}`} className="min-h-[18px] h-[18]"></div>;
      });
    }

    return null;
  }, [tariffToolsMaxCount, toolItems.length]);

  return (
    <div
      className={classNames(
        `
        relative
        font-manrope
        bg-white
        border-1
        rounded-3xl
        px-4
        pt-10
        flex
        flex-col
        w-[272px]
        min-w-[272px]
        md:h-full
      `,
        {
          'max-h-[456px]  h-[456px] overflow-hidden': isShotMode,
          'border-[#C00000]': isPromoAction,
          'border-[#DEC05E]': !isPromoAction,
        },
      )}
    >
      {tariff.is_popular && !isPromoAction && (
        <div className="absolute top-0 right-6  w-[100px] h-[45px]">
          <TariffCardLabel className={'bg-[#DEC05E]'} title="популярное" />
        </div>
      )}

      {isPromoAction && (
        <div className="absolute top-0 right-6  w-[100px] h-[45px]">
          <TariffCardLabel className={'bg-[#C00000] text-[#FFFFFF]'} title="акция" />
        </div>
      )}

      <p className="font-manrope text-[16px] text-center text-black mb-1 min-h-[24px]">
        {tariff.duration_days === 'unlimit' ? 'бессрочно' : `${tariff.duration_days} дней`}
      </p>
      <TariffCardHeader tariff={tariff} showEmptyDescription1={false} />
      {tariff.description_2 && tariff.description_2.trim() !== '' && (
        <p className="font-manrope text-[12px] text-center text-[#737373] mb-2 min-h-[18px]">{tariff.description_2}</p>
      )}

      <BonusInfoLabel
        limit={tariff.bonus_payment_limit_percent}
        classes={`${!tariff.is_bonus_payment_available ? 'invisible' : ''} text-gray-500`}
      />

      <div className={classNames('mb-3', { invisible: !hasDiscount || basePrice <= 0 })}>
        <p className="font-manrope text-[12px] text-center text-black mb-1">
          стоимость за {baseDuration} дней
        </p>
        <div className="flex items-end gap-2">
          <div className="flex items-end gap-2">
            <p className="font-manrope text-[16px] text-[#737373] line-through">
              {basePrice.toLocaleString('ru-RU')} {tariff.currency_symbol}
            </p>
            <p className="font-manrope text-[16px] text-black">
              {discountedBasePrice.toLocaleString('ru-RU')} {tariff.currency_symbol}
            </p>
          </div>
          <p className="font-manrope text-[12px] text-black whitespace-nowrap ml-auto">
            скидка <span className="text-red-600 text-[16px] font-bold">{discount}%</span>
          </p>
        </div>
      </div>

      <>
        <div
          className={classNames('rounded-lg px-4 text-center min-h-[34px] h-[34px] flex items-center', {
            'bg-gray-100': active || isFree,
            'bg-black ': !active && !isFree,
          })}
        >
          {active && (
            <p className="text-gray-500 text-[16px] font-semibold font-comfortaa w-full text-center">
              Активно
              {start_end_date && ` До ${formatDateLocaleDDMMYY(new Date(start_end_date), 'ru-RU')}`}
            </p>
          )}
          {!active && (
            <button
              className={classNames('w-full h-full   text-[16px] font-semibold font-comfortaa', {
                'cursor-pointer': !isFree,
                'text-white': !isFree,
                'text-gray-500': isFree,
                'pointer-events-none opacity-60': selectDisabled,
              })}
              disabled={selectDisabled}
              onClick={() => {
                if (!selectDisabled) {
                  onSelectClick?.();
                }
              }}
            >
              {isFree && !scheduled && 'Бесплатно'}
              {!isFree && !scheduled && 'Выбрать'}
              {scheduled && start_end_date && `Активно С ${formatDateLocaleDDMMYY(new Date(start_end_date), 'ru-RU')}`}
            </button>
          )}
        </div>
        <button
          className={classNames(
            'text-[12px] cursor-pointer h-[16px] max-h-[16px] min-h-[16px] text-[#C00000] font-manrope font-medium underline mt-[5px]',
            {
              invisible: !active || tariff.type !== TariffTypes.REGULAR || hideRenew,
            },
          )}
          onClick={onSelectClick}
        >
          продлить тариф
        </button>
        <ul className="flex-grow mt-1">
          {toolItems.slice(0, isShotMode ? 8 : undefined).map((tool, index) => {
            const isUnavailable = tool.limit !== null && tool.limit !== undefined && Number(tool.limit) === 0;

            return (
              <li key={`${tool.name ?? tool.tool_name}-${index}`} className="flex items-center">
                {isUnavailable ? (
                  <Cross fill="#9CA3AF" className="mr-1" />
                ) : (
                  <Check fill="#C00000" className="mr-1" />
                )}
                <div className="flex items-center min-w-0">
                  <span className="font-manrope text-[12px] whitespace-nowrap overflow-hidden text-ellipsis">
                    {tool.tool_name || tool.name}
                  </span>
                  {tool.tool_description && tool.tool_description.trim() !== '' && (
                    <ToolDescriptionIcon description={tool.tool_description} />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {!isShotMode && tariffToolsFiller}
        {isShotMode && (
          <>
            <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-white to-transparent pointer-events-none z-[10]"></div>
            <div className="text-center z-[11] absolute bottom-0 left-0 right-0 bg-white h-[18px] flex justify-center items-end">
              <button
                className="text-sm hover:underline mb-2 cursor-pointer"
                onClick={onDetailsClick ? () => onDetailsClick() : undefined}
              >
                подробнее
              </button>
            </div>
          </>
        )}
        {!isShotMode && <div className="mb-5"></div>}
      </>
    </div>
  );
};
